import React, { useEffect, useMemo, useState } from 'react'
import { Box, Typography, styled } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { useNavigate, useParams } from 'react-router-dom'
import { EmptyState, IconButton, SearchInput, Skeleton } from '@/design-system'
import { useGetBasicInfo } from '@/hooks/api/dashboard/useGetBasicInfo'
import { useGetUserConnections } from '@/hooks/api/dashboard'
import { ConnectionModel } from '@/types'
import { ConnectionsPreviewGrid } from '../../components/ConnectionsPreviewGrid/ConnectionsPreviewGrid'

const Page = styled(Box)({ maxWidth: 920 })

const PageHead = styled(Box)({ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 22 })

const H1 = styled(Typography)({ fontSize: 24, fontWeight: 720, letterSpacing: '-0.03em', color: 'var(--ds-ink)' })

const Sub = styled(Typography)({ fontSize: 12.5, color: 'var(--ds-ink-subtle)', marginTop: 3 })

const SearchWrap = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  height: 36,
  padding: '0 12px',
  borderRadius: 'var(--ds-radius-md)',
  background: 'var(--ds-canvas)',
  boxShadow: 'var(--ds-shadow-soft)',
  border: '1px solid var(--ds-border)',
  marginBottom: 20,
  maxWidth: 360,
  '&:focus-within': { borderColor: 'var(--ds-ink-muted)' },
})

const LoadMoreBtn = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  display: 'block',
  width: '100%',
  marginTop: 18,
  padding: '10px 0',
  textAlign: 'center',
  fontSize: 13,
  fontWeight: 620,
  color: 'var(--ds-ink-subtle)',
  cursor: 'pointer',
  borderRadius: 'var(--ds-radius-md)',
  background: 'var(--ds-hover)',
  transition: 'background var(--ds-duration-fast) var(--ds-ease), color var(--ds-duration-fast) var(--ds-ease)',
  '&:hover': { background: 'var(--ds-border)', color: 'var(--ds-ink)' },
  '&:disabled': { opacity: 0.4, cursor: 'not-allowed' },
})

const PAGE_SIZE = 24

function matchesQuery(conn: ConnectionModel, q: string): boolean {
  if (!q) return true
  const haystack = `${conn.firstName} ${conn.lastName} ${conn.profession} ${conn.location}`.toLowerCase()
  return haystack.includes(q.toLowerCase())
}

export function UserConnectionsScreen() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()

  const getBasicInfo = useGetBasicInfo(userId ?? '')
  const getUserConnections = useGetUserConnections(userId ?? '')

  const [name, setName] = useState('')
  const [allConnections, setAllConnections] = useState<ConnectionModel[]>([])
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)

  useEffect(() => {
    if (!userId) return

    getBasicInfo.mutateAsync({ isUserProfile: false }).then((info) => {
      if (info?.firstName) setName(`${info.firstName} ${info.lastName}`.trim())
    })

    getUserConnections.mutateAsync(1).then((data) => {
      setAllConnections(data as unknown as ConnectionModel[])
      setPage(1)
      setHasMore((data?.length ?? 0) >= PAGE_SIZE)
      setLoading(false)
    }).catch(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId])

  const handleLoadMore = async () => {
    const nextPage = page + 1
    setLoadingMore(true)
    try {
      const data = await getUserConnections.mutateAsync(nextPage)
      setAllConnections((prev) => [...prev, ...(data as unknown as ConnectionModel[])])
      setPage(nextPage)
      setHasMore((data?.length ?? 0) >= PAGE_SIZE)
    } finally {
      setLoadingMore(false)
    }
  }

  const filtered = useMemo(() => allConnections.filter((c) => matchesQuery(c, query)), [allConnections, query])

  const displayName = name || 'Their'
  const connCount = allConnections.length

  return (
    <Page>
      <PageHead>
        <IconButton tone='quiet' aria-label='Back' onClick={() => navigate(`/profile/${userId}`)}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <H1>{displayName}&apos;s connections</H1>
          <Sub>{connCount} {connCount === 1 ? 'person' : 'people'} in their circle</Sub>
        </Box>
      </PageHead>

      {!loading && allConnections.length > 0 && (
        <SearchWrap>
          <SearchInput
            icon='⌕'
            placeholder='Filter by name…'
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{ fontSize: 13 }}
          />
        </SearchWrap>
      )}

      {loading ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} width='100%' height={60} rounded='md' />
          ))}
        </Box>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<span style={{ fontSize: 32, opacity: 0.35 }}>◎</span>}
          title={allConnections.length === 0 ? 'No connections visible' : 'No matches'}
          description={
            allConnections.length === 0
              ? "Their connections list is private or they haven't connected with anyone yet."
              : 'Try a different name.'
          }
        />
      ) : (
        <>
          <ConnectionsPreviewGrid
            connections={filtered}
            onPersonClick={(conn) => navigate(`/profile/${conn.userID}`)}
          />
          {hasMore && !query && (
            <LoadMoreBtn onClick={handleLoadMore} disabled={loadingMore}>
              {loadingMore ? 'Loading…' : 'Load more'}
            </LoadMoreBtn>
          )}
        </>
      )}
    </Page>
  )
}
